import { Clipboard } from '@capacitor/clipboard';
import { Share } from '@capacitor/share';
import { Grid, Slide } from '@mui/material';
import { CrossIcon, RoutalPalette } from '@nx-smartmonkey/ui';
import { createPortal } from 'react-dom';

import { capacitorService } from '../../../../application/CapacitorService';
import HeaderBackground from '../../../../assets/img/referral-modal/header-background.png';
import MoneyIcon from '../../../../assets/img/referral-modal/money.svg';
import ReceiverIcon from '../../../../assets/img/referral-modal/receiver.svg';
import SenderIcon from '../../../../assets/img/referral-modal/sender.svg';
import { DRIVERS_LAYOUT_ID } from '../../../../components/DriversLayout';
import { EventTrackTypes } from '../../../../constants/EventTracks';
import { useCaptureEvents } from '../../../../hooks/useCaptureEvents';
import { useShowSnackbarMessage } from '../../../../hooks/useShowSnackbarMessage';
import Button from '../../../../react-components/Button';
import { Text } from '../../../../react-components/Text';
import { useRetrieveVehicle } from '../../hooks/useRetrieveVehicle';

interface StepInfoProps {
  iconContent: React.ReactElement | string;
  titleIntlId: string;
  subtitleIntlId: string;
}

const StepInfo = ({ iconContent, titleIntlId, subtitleIntlId }: StepInfoProps) => {
  return (
    <Grid container direction="row" gap="8px" wrap="nowrap" alignItems="center">
      <div
        style={{
          display: `flex`,
          justifyContent: `center`,
          alignItems: `center`,
          width: `22px`,
          height: `22px`,
          borderRadius: `100px`,
          background: RoutalPalette.primary80,
        }}
      >
        {iconContent}
      </div>
      <Grid container direction="column" gap="2px" style={{ width: `calc(100% - 20px)` }}>
        <Text variant="body" intlId={titleIntlId} style={{ color: RoutalPalette.primary80, fontWeight: 500 }} />
        <Text variant="small" intlId={subtitleIntlId} style={{ color: RoutalPalette.secondary00, fontWeight: 400 }} />
      </Grid>
    </Grid>
  );
};

interface ReferralSlideProps {
  onClose: () => void;
  openSlide: boolean;
}

export const ReferralSlide = ({ openSlide, onClose }: ReferralSlideProps) => {
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { data: vehicleDetails } = useRetrieveVehicle();
  const { capture } = useCaptureEvents();

  if (!vehicleDetails) return null;

  const referralLink = `https://drivers.routal.com/auth?referral_id=${vehicleDetails.vehicle.organization_id}`;

  const writeToClipboard = async () => {
    if (capacitorService.isNative() && (await Share.canShare())) {
      Share.share({
        // title: `See cool stuff`,
        // text: `Really awesome thing you need to see right meow`,
        url: referralLink,
        // dialogTitle: `Share with buddies`,
      });
    } else {
      await Clipboard.write({ string: referralLink });

      showSnackbarMessage({
        type: `success`,
        messageIntlId: `drivers.referral_dialog.link_copied`,
      });
    }
  };

  const receiverInfo = [
    {
      iconContent: (
        <Text
          tagName="span"
          variant="small"
          style={{
            color: RoutalPalette.neutral02,
          }}
        >
          1
        </Text>
      ),
      titleIntlId: `drivers.referral_dialog.receiver.step1.title`,
      subtitleIntlId: `drivers.referral_dialog.receiver.step1.subtitle`,
    },
    {
      iconContent: (
        <Text
          tagName="span"
          variant="small"
          style={{
            color: RoutalPalette.neutral02,
          }}
        >
          2
        </Text>
      ),
      titleIntlId: `drivers.referral_dialog.receiver.step2.title`,
      subtitleIntlId: `drivers.referral_dialog.receiver.step2.subtitle`,
    },
  ];

  const senderInfo = [
    {
      iconContent: <img src={MoneyIcon} alt="" width="12" />,
      titleIntlId: `drivers.referral_dialog.sender.step1.title`,
      subtitleIntlId: `drivers.referral_dialog.sender.step1.subtitle`,
    },
  ];

  const element = document.getElementById(DRIVERS_LAYOUT_ID);
  if (!element) {
    return null;
  }

  return createPortal(
    <Slide
      key="referral-slide"
      in={openSlide}
      direction="down"
      mountOnEnter
      unmountOnExit
      timeout={{ enter: 400, exit: 300 }}
      style={{ top: 0, zIndex: 10010, position: `absolute` }}
    >
      <Grid
        style={{
          height: `100%`,
          width: `100%`,
          overflowY: `hidden`,
          background: RoutalPalette.white,
        }}
      >
        <Grid
          container
          style={{
            position: `relative`,
            borderBottomLeftRadius: `50px`,
            borderBottomRightRadius: `50px`,
            backgroundImage: `url(${HeaderBackground})`,
            backgroundSize: `cover`,
            backgroundPosition: `center`,
            height: `33%`,
            maxHeight: `300px`,
          }}
        >
          <CrossIcon
            style={{
              position: `absolute`,
              color: RoutalPalette.white,
              height: `32px`,
              width: `32px`,
              fontSize: `32px`,
              cursor: `pointer`,
              top: `20px`,
              left: `20px`,
            }}
            onClick={() => onClose()}
          />
        </Grid>

        <Grid
          container
          direction="column"
          gap="16px"
          style={{ padding: `20px`, height: `66%`, flexWrap: `nowrap`, justifyContent: `space-between` }}
        >
          <Text variant="h5" intlId="drivers.referral_dialog.title" />

          <Grid
            container
            direction="column"
            alignItems="center"
            style={{
              height: `100%`,
            }}
            gap="12px"
          >
            <Grid container direction="column" wrap="nowrap" style={{ flex: 1 }} gap="12px">
              <Grid
                container
                direction="column"
                gap="14px"
                style={{
                  padding: `16px`,
                  backgroundColor: RoutalPalette.neutral02,
                  borderRadius: `10px`,
                }}
              >
                <Grid container direction="row" gap="8px">
                  <img src={ReceiverIcon} alt="" />
                  <Text
                    variant="body"
                    style={{ color: RoutalPalette.secondary00 }}
                    intlId="drivers.referral_dialog.receiver.title"
                  />
                </Grid>

                {receiverInfo.map((item, index) => (
                  <StepInfo
                    key={index}
                    iconContent={item.iconContent}
                    titleIntlId={item.titleIntlId}
                    subtitleIntlId={item.subtitleIntlId}
                  />
                ))}
              </Grid>

              <Grid
                container
                direction="column"
                gap="14px"
                style={{
                  padding: `16px`,
                  backgroundColor: RoutalPalette.neutral02,
                  borderRadius: `10px`,
                }}
              >
                <Grid container direction="row" gap="8px">
                  <img src={SenderIcon} alt="" />
                  <Text
                    variant="body"
                    style={{ color: RoutalPalette.secondary00 }}
                    intlId="drivers.referral_dialog.sender.title"
                  />
                </Grid>

                {senderInfo.map((item, index) => (
                  <StepInfo
                    key={index}
                    iconContent={item.iconContent}
                    titleIntlId={item.titleIntlId}
                    subtitleIntlId={item.subtitleIntlId}
                  />
                ))}
              </Grid>
            </Grid>

            <Grid container direction="row">
              <Button
                style={{ width: `100%` }}
                intlId="drivers.referral_dialog.send_invitation"
                onClick={() => {
                  capture({
                    type: EventTrackTypes.ReferralLink,
                    payload: {
                      [`Action`]: `Copy`,
                    },
                  });
                  writeToClipboard();
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Slide>,
    element as HTMLElement
  );
};
